/* tslint:disable:max-line-length */
import {Injectable} from '@angular/core';
import {Attachment, AttachmentSource} from '@docma-mm-core/attachment/attachment';
import {
  AnnoChanges,
  AnnoLayerToImageService,
  AnnotationService,
  BaseAnnotation,
  Size
} from '@mydocma/component-library/src/lib/annotation';
import {AttachmentService} from '@docma-mm-core/attachment/attachment.service';
import {Converters} from './converters/converter';
import {MmFaultsLoaderService} from '../../../mm-faults-loader.service';
import {dataURItoBlob, downscaleFileIfNeeded, fileToImage, fileToManagedImage, scaleBlob} from './helpers/util';
import {EMPTY, firstValueFrom, from, of, Subject} from 'rxjs';
import {MmProjectService} from '../../../../mm-project/mm-project.service';
import {Scribble} from '@docma-mm-core/scribble/scribble';
import {ChangeState} from '@docma-mm-core/model/change-state/change-state';
import {ChangeStatusEnum} from '@docma-mm-core/model/change-state/change-status.enum';
import {ScribbleLayerData} from '@docma-mm-core/scribble/scribble-layers';
import {FaultService} from '@docma-mm-core/fault/fault.service';
import {catchError, concatMap, filter, first, map, switchMap, tap} from 'rxjs/operators';
import {MmProjectLoaderService} from '../../../../mm-project/mm-project-loader.service';
import {User} from '@docma-mm-core/user/user';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {AnnotationPermissionsService} from './annotation-permissions.service';
import {ViewFault} from '@docma-mm-core/fault/fault';
import {setSuspended} from '@docma-mm-core/utils/utils';
import * as ExifReader from 'exifreader';
import {Logger} from '@mydocma/common/src/lib/logger';
import {DeviceService} from "@mydocma/core/src/lib/device";

interface SaveChangesQueueItem {
  res: AnnoChanges;
  attachment: Attachment | null;
  currentFault: ViewFault | null;
  models: BaseAnnotation[];
  containerSize: Size;
  resolve: (result: boolean) => void;
}

@UntilDestroy()
@Injectable({providedIn: 'root'})
export class MmAnnotationService {
  private onChangesCommittedSubject = new Subject<AnnoChanges>();
  public onChangesCommitted = this.onChangesCommittedSubject.asObservable();
  protected annotationService: AnnotationService;
  private _attachment: Attachment;
  private attachmentSubject = new Subject<Attachment>();
  private currentUser: User;
  public currentFault: ViewFault;
  private suspendLoad = false;

  private readonly saveChangesQueue$ = new Subject<SaveChangesQueueItem>();

  constructor(
    private readonly attachmentService: AttachmentService,
    private readonly annoLayerToImageService: AnnoLayerToImageService,
    private readonly faultsLoaderService: MmFaultsLoaderService,
    protected readonly mmProjectService: MmProjectService,
    private readonly faultService: FaultService,
    private readonly projectLoaderService: MmProjectLoaderService,
    private readonly annotationPermissionsService: AnnotationPermissionsService,
    private readonly logger: Logger,
    private deviceService: DeviceService,
  ) {
    this.projectLoaderService.projectUserPermissions
      .pipe(untilDestroyed(this))
      .subscribe(userPerms => {
        if (userPerms.loadState.status === LoadStatusEnum.Loaded) {
          this.currentUser = userPerms.data.User;
        }
      });

    this.attachmentSubject
      .pipe(
        switchMap(attachment => {
          if (attachment?.scribbles$) {
            return attachment.scribbles$.pipe(filter(scribbles => !!scribbles));
          }
          return of(null);
        }),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.setAnnotations();
      });
    this.saveChangesQueue$
      .pipe(
        concatMap(item =>
          from(this.performSaveChanges(item)).pipe(
            tap(result => item.resolve(result)),
            catchError(error => {
              this.logger.error(
                'Queued annotation save failed, attachmentUid: ' + item.attachment?.uid + ', attachmentId: ' + item.attachment?.data?.Id + ', error: ' + error,
                '',
                'MmAnnotationService',
              );
              item.resolve(false);
              return EMPTY;
            }),
          ),
        ),
        untilDestroyed(this),
      )
      .subscribe();

    this.attachmentService.attachTo(this);
  }

  attachTo(annotationService: AnnotationService) {
    this.annotationService = annotationService;
    annotationService.saveChanges = (res): Promise<boolean> => this.enqueueSaveChanges(res);
  }

  private enqueueSaveChanges(res: AnnoChanges): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      this.saveChangesQueue$.next({
        res,
        attachment: this._attachment ?? null,
        currentFault: this.currentFault ?? null,
        models: [...((this.annotationService?.annotations || []) as BaseAnnotation[])],
        containerSize: {...this.annotationService.containerSize},
        resolve,
      });
    });
  }

  private async performSaveChanges(item: SaveChangesQueueItem): Promise<boolean> {
    const {res, attachment, currentFault, models, containerSize} = item;

    try {
      if (!attachment) {
        return false;
      }

      const currentProject = await firstValueFrom(this.mmProjectService.currentProject);

      const bgFile: File = attachment.source?.loadState?.data instanceof File
        ? (attachment.source.loadState.data as File)
        : await this.attachmentService.getRawAttachmentFile(
            attachment,
            currentProject?.MyDocmaUid,
            !!currentFault?.downloaded,
          );
      if (!bgFile) {
        throw new Error('Attachment source data is missing');
      }
      // const sourceForExport = this.deviceService.isMobil() ?
      //   await downscaleFileIfNeeded(bgFile, 2000) : bgFile;


      const {image: background, dispose} = await fileToManagedImage(bgFile);
      try {
        const {preview, thumbnail} = await this.annoLayerToImageService.generateImageBlobsAnnotations(
          models,
          background,
        );

        const fileName = `${attachment.data.Description || 'annotated-thumb'}.png`;
        const now = Date.now();
        const previewFile = new File([preview], fileName, {
          type: 'image/png',
          lastModified: now,
        });
        const thumbnailFile = new File([thumbnail], fileName, {
          type: 'image/png',
          lastModified: now,
        });

        this.attachmentService.setAttachmentSource(
          (attachment.preview ??= new AttachmentSource()),
          previewFile,
        );
        this.attachmentService.setAttachmentSource(
          (attachment.thumbnail ??= new AttachmentSource()),
          thumbnailFile,
        );
      } finally {
        dispose();
      }

      const attachmentScribblesDict: Record<string, Scribble> = Object.fromEntries(
        (attachment.scribbles ?? []).map(x => [x.uid, x]),
      );

      const userPermission = await firstValueFrom(
        this.projectLoaderService.projectUserPermissions.pipe(
          filter(userPerm => userPerm.loadState.status === LoadStatusEnum.Loaded),
          map(x => x.data),
        ),
      );
      const currentUser = userPermission.User;

      const sl = attachment.data?.ScribbleLayers?.find(x => x.Creator.Id === currentUser.Id);

      const currentScribbleLayer: ScribbleLayerData = sl
        ? {
          Id: sl.Id,
          UniqueId: sl.UniqueId,
        }
        : {};

      currentScribbleLayer.File = {UniqueId: attachment.uid};

      const conv = new Converters();

      const changes = [
        ...res.added.map(anno => ({status: ChangeStatusEnum.New, anno})),
        ...res.updated.map(anno => ({status: ChangeStatusEnum.Changed, anno})),
        ...res.deleted.map(anno => ({status: ChangeStatusEnum.Deleted, anno})),
      ];

      for (const change of changes) {
        const scribbleData = conv.toData(change.anno, containerSize);

        if (this.currentUser) {
          if (change.status === ChangeStatusEnum.New) {
            scribbleData.Creator = {
              Id: this.currentUser.Id,
              Account: this.currentUser.Account,
              FirstName: this.currentUser.FirstName,
              LastName: this.currentUser.LastName,
            } as User;
            scribbleData.CreatedOn = new Date();
          } else if (change.status === ChangeStatusEnum.Changed) {
            scribbleData.Modifier = {
              Id: this.currentUser.Id,
              Account: this.currentUser.Account,
              FirstName: this.currentUser.FirstName,
              LastName: this.currentUser.LastName,
            } as User;
            scribbleData.ModifiedOn = new Date();
          }
        }

        if (scribbleData.UniqueId && attachmentScribblesDict.hasOwnProperty(scribbleData.UniqueId)) {
          scribbleData.Id = attachmentScribblesDict[scribbleData.UniqueId]?.data.Id;
        }

        scribbleData.ScribbleLayer = currentScribbleLayer;

        const existingScribble =
          scribbleData.UniqueId ? attachmentScribblesDict[scribbleData.UniqueId] : null;

        const scribble = existingScribble ?? new Scribble();

        scribble.addChanges(scribbleData);

        if (scribbleData.UniqueId && !existingScribble) {
          attachment.addScribbles([scribble]);
          attachmentScribblesDict[scribbleData.UniqueId] = scribble;
        }

        scribble.changeState = new ChangeState({
          data: scribble.data,
          status: change.status,
          date: scribble.data.ModifiedOn ?? new Date(),
        });
      }

      if (currentFault && !currentFault.unsavedNew) {
        await this.faultService.saveFaultChanges(
          currentProject.MyDocmaUid,
          currentFault,
          currentFault?.changeState?.status !== ChangeStatusEnum.New,
        );
      }

      const affectedFaults = await firstValueFrom(
        this.faultsLoaderService.faults.pipe(
          map(x =>
            x.loadedFaults.filter(f =>
              f.layoutState.loadState.data.LastImage === (attachment?.id ?? -1) ||
              f.layoutState.loadState.data.FirstImage === (attachment?.id ?? -1) ||
              f.layoutState.loadState.data.LastPlanSection === (attachment?.id ?? -1) ||
              f.layoutState.loadState.data.FirstPlanSection === (attachment?.id ?? -1),
            ),
          ),
        ),
      );

      const affectedLoadedFaults = await firstValueFrom(
        this.faultsLoaderService.faults.pipe(
          map(x =>
            x.loadedFaults.filter(
              f =>
                f.loadState.status === LoadStatusEnum.Loaded &&
                (
                  f.attachments?.some(a => a?.id === attachment?.id || a?.uid === attachment?.uid) ||
                  f.activities?.flatMap(a => a.attachments)?.some(a => a.id === attachment.id)
                ),
            ),
          ),
        ),
      );

      await Promise.all(
        affectedLoadedFaults.map(async fault => {
          const faultAttachmentsById = Object.fromEntries(
            fault.attachments?.filter(x => !!x.id)?.map(x => [x.id, x]) ?? [],
          );
          const faultAttachmentsByUid = Object.fromEntries(
            fault.attachments?.filter(x => !x.id)?.map(x => [x.uid, x]) ?? [],
          );

          const found0 = faultAttachmentsById.hasOwnProperty(attachment.id)
            ? faultAttachmentsById[attachment.id]
            : faultAttachmentsByUid.hasOwnProperty(attachment.uid)
              ? faultAttachmentsByUid[attachment.uid]
              : null;

          if (found0) {
            Object.assign(found0, attachment);
          } else {
            const activitiesAttachmentsById = Object.fromEntries(
              fault.activities?.flatMap(x => x?.attachments ?? [])?.filter(x => !!x.id)?.map(x => [x.id, x]) ?? [],
            );
            const activitiesAttachmentsByUid = Object.fromEntries(
              fault.activities?.flatMap(x => x?.attachments ?? [])?.filter(x => !x.id)?.map(x => [x.uid, x]) ?? [],
            );

            const found1 = activitiesAttachmentsById.hasOwnProperty(attachment.id)
              ? activitiesAttachmentsById[attachment.id]
              : activitiesAttachmentsByUid.hasOwnProperty(attachment.uid)
                ? activitiesAttachmentsByUid[attachment.uid]
                : null;

            if (found1) {
              setSuspended(this, 'suspendLoad', () => {
                found1.scribbles = attachment.scribbles;
                found1.thumbnail = attachment.thumbnail;
                found1.preview = attachment.preview;
              });
            }
          }
        }),
      );

      await Promise.all(
        affectedFaults.map(async fault => {
          const faultAttachmentsById = Object.fromEntries(
            fault.attachments?.filter(x => !!x.id)?.map(x => [x.id, x]) ?? [],
          );
          const faultAttachmentsByUid = Object.fromEntries(
            fault.attachments?.filter(x => !x.id)?.map(x => [x.uid, x]) ?? [],
          );

          const found0 = faultAttachmentsById.hasOwnProperty(attachment.id)
            ? faultAttachmentsById[attachment.id]
            : faultAttachmentsByUid.hasOwnProperty(attachment.uid)
              ? faultAttachmentsByUid[attachment.uid]
              : null;

          if (found0) {
            found0.thumbnail = attachment.thumbnail;
          } else {
            const activitiesAttachmentsById = Object.fromEntries(
              fault.activities?.flatMap(x => x?.attachments ?? [])?.filter(x => !!x.id)?.map(x => [x.id, x]) ?? [],
            );
            const activitiesAttachmentsByUid = Object.fromEntries(
              fault.activities?.flatMap(x => x?.attachments ?? [])?.filter(x => !x.id)?.map(x => [x.uid, x]) ?? [],
            );

            const found1 = activitiesAttachmentsById.hasOwnProperty(attachment.id)
              ? activitiesAttachmentsById[attachment.id]
              : activitiesAttachmentsByUid.hasOwnProperty(attachment.uid)
                ? activitiesAttachmentsByUid[attachment.uid]
                : null;

            if (found1) {
              found1.thumbnail = attachment.thumbnail;
            }
          }

          await this.attachmentService.changeAttachment(attachment, fault);
        }),
      );

      return true;
    } catch (e) {
      this.logger.error(
        'Failed to commit annotation changes, attachmentUid: ' + attachment?.uid + ', attachmentId: ' + attachment?.data?.Id + ', error: ' + e,
        '',
        'MmAnnotationService',
      );
      return false;
    } finally {
      this.onChangesCommittedSubject.next(res);
    }
  }

  set attachment(value: Attachment) {
    this._attachment = value;
    this.attachmentSubject.next(value);
    this.setAnnotations();
  }

  get attachment(): Attachment {
    return this._attachment;
  }

  private async setAnnotations() {
    if (!this._attachment || !this.annotationService || !this.annotationService.containerSize) {
      return;
    }

    if (!this._attachment.scribbles || this._attachment.scribbles.length === 0) {
      this.annotationService.setAnnotations([]);
      return;
    }

    if (!!this.suspendLoad) {
      return;
    }

    const scribbles = this._attachment.scribbles
      .filter(s => s.changeState?.status !== ChangeStatusEnum.Deleted)
      .map(x => x.data);

    const conv = new Converters();

    const supportedScribbles = scribbles.filter(s => conv.supportsScribble(s));

    const annos = supportedScribbles
      .map(s => conv.toModel(s, this.annotationService.containerSize)) as BaseAnnotation[];

    const hasCreate = await firstValueFrom(this.annotationPermissionsService.permissionCreate$);
    const hasUpdate = await firstValueFrom(this.annotationPermissionsService.permissionUpdate$);
    const hasDelete = await firstValueFrom(this.annotationPermissionsService.permissionDelete$);

    annos.forEach((anno, index) => {
      const scribbleData = supportedScribbles[index];
      const isOwn = this.annotationPermissionsService.isOwnAnnotation(scribbleData, this.currentUser);

      anno.permissions = {
        edit: (hasCreate && isOwn) || hasUpdate,
        delete: (hasCreate && isOwn) || hasDelete,
        move: (hasCreate && isOwn) || hasUpdate,
        resizable: (hasCreate && isOwn) || hasUpdate,
      };
    });

    this.annotationService.setAnnotations(annos);
  }

  async getCompositeImage(file: File, scribbles: Scribble[], imageSize: Size, size?: {
    height: number,
    width: number
  }) {
    const conv = new Converters();
    const annos = scribbles
      .filter(s => conv.supportsScribble(s.data))
      .map(s => conv.toModel(s.data, imageSize)) as BaseAnnotation[];

    const background = await fileToImage(file);
    const models = (annos || []) as BaseAnnotation[];
    const dataURI = this.annoLayerToImageService.generateImageWithAnnotations(models, background);
    const bakedBlob = dataURItoBlob(dataURI);
    const sizedBlob = size?.width && size?.height
      ? await scaleBlob(bakedBlob, size.width, size.height)
      : bakedBlob;
    const compositeFile = new File([sizedBlob], file.name, {
      type: sizedBlob.type || 'image/png',
      lastModified: Date.now(),
    });
    return compositeFile;
  }

  async downloadAttachment(attachment: Attachment) {
    const source = await this.attachmentService.getAttachmentSource(attachment, true);
    await source.loadState$
      .pipe(filter(ls => ls.status === LoadStatusEnum.Loaded || ls.status === LoadStatusEnum.Error), first())
      .toPromise();

    if (source.loadState.status === LoadStatusEnum.Error || !attachment.source?.url) {
      this.logger.error('Attachment source failed to load, uid: ' + attachment?.uid + ', id: ' + attachment?.data?.Id + ', status: ' + source?.loadState?.status + ', hasUrl: ' + !!attachment?.source?.url, '', 'MmAnnotationService');
      throw new Error('Attachment source failed to load');
    }

    const bgFile = source.loadState.data as File;
    if (!bgFile) {
      this.logger.error('Attachment source data is missing, uid: ' + attachment?.uid + ', id: ' + attachment?.data?.Id + ', status: ' + source?.loadState?.status + ', hasUrl: ' + !!attachment?.source?.url, '', 'MmAnnotationService');
      throw new Error('Attachment source data is missing');
    }

    const background = await fileToImage(bgFile);
    const imageWidth = (attachment.data as any).ImageWidth || background.naturalWidth || background.width;
    const imageHeight = (attachment.data as any).ImageHeight || background.naturalHeight || background.height;
    const imageContainerSize: Size = {
      width: imageWidth,
      height: imageHeight,
    };

    const scribbles = attachment.scribbles.map(x => x.data);
    const conv = new Converters();
    const annos = scribbles
      .filter(s => conv.supportsScribble(s))
      .map(s => conv.toModel(s, imageContainerSize)) as BaseAnnotation[];

    const models = (annos || []) as BaseAnnotation[];
    const dataURI = this.annoLayerToImageService.generateImageWithAnnotations(models, background);
    const bakedBlob = dataURItoBlob(dataURI);

    const originalFileName = attachment.data.OriginalFileName || attachment.data.FileName || attachment.data.Description || 'annotated';
    const fileNameWithoutExt = originalFileName.replace(/\.[^/.]+$/, '');
    const fullFile = new File([bakedBlob], `${fileNameWithoutExt}.png`, {
      type: 'image/png',
      lastModified: Date.now(),
    });

    const downloadSource = new AttachmentSource();
    this.attachmentService.setAttachmentSource(downloadSource, fullFile);

    await this.attachmentService.downloadAttachment(attachment, downloadSource);
  }
}
